import { useMutation } from "@tanstack/react-query";
import { ApiError, Button, ErrorAlert } from "@unizpay/ui";
import { useEffect, useRef, useState } from "react";
import type { SecurityCheckStart } from "../lib/types";
import { Dialog } from "./Dialog";
import { PinBoxes } from "./PinBoxes";

const RESEND_AFTER_S = 60;

/**
 * Step one of approving a sensitive action: the 6-digit code emailed for it, checked on its own
 * (`verify`) before the PIN is asked for. Emails the code when it first opens, unless `sent` is
 * one already asked for; mount it with a new `key` for a new code. Wrong codes count toward the
 * same limit as the final check.
 */
export function EmailCodeDialog({
  open,
  request,
  verify,
  onVerified,
  onCancel,
  notice,
  sent,
}: {
  open: boolean;
  /** Emails a code for this exact action. */
  request: () => Promise<SecurityCheckStart>;
  verify: (start: SecurityCheckStart, code: string) => Promise<unknown>;
  onVerified: (start: SecurityCheckStart, code: string) => void;
  onCancel: () => void;
  /** Why a new code was needed, e.g. the last one expired. */
  notice?: string | null;
  /** A code the page already asked for (it had to ask whether one was needed): not sent again. */
  sent?: SecurityCheckStart;
}) {
  const [start, setStart] = useState<SecurityCheckStart | null>(sent ?? null);
  const [code, setCode] = useState("");
  const [wait, setWait] = useState(sent ? RESEND_AFTER_S : 0);
  const send = useMutation({
    mutationFn: request,
    onSuccess: (s) => {
      setStart(s);
      setCode("");
      setWait(RESEND_AFTER_S);
    },
  });
  const check = useMutation({
    mutationFn: (typed: string) => verify(start!, typed),
    onSuccess: (_, typed) => onVerified(start!, typed),
    onError: () => setCode(""),
  });

  // One code per mount, even when React runs effects twice in development.
  const asked = useRef(Boolean(sent));
  useEffect(() => {
    if (!open || asked.current) return;
    asked.current = true;
    send.mutate();
  }, [open, send]);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const fieldError =
    check.error instanceof ApiError && check.error.code === "code_incorrect" ? check.error.message : null;
  const expired = check.error instanceof ApiError && check.error.code === "code_expired";
  const submit = (typed: string) => {
    if (start && typed.length === 6 && !check.isPending) check.mutate(typed);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title="Email verification"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={check.isPending}>
            Cancel
          </Button>
          <Button onClick={() => submit(code)} loading={check.isPending} disabled={!start || code.length < 6}>
            Continue
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-fg">
        {notice && <p className="text-xs text-warn">{notice}</p>}
        <p className="text-xs text-fg-muted">
          {send.isPending ? "Sending code…" : start ? `Code sent to ${start.email}` : "Code not sent"}
        </p>
        <PinBoxes
          label="Email code"
          value={code}
          onChange={(v) => {
            setCode(v);
            if (check.isError) check.reset();
          }}
          onComplete={submit}
          masked={false}
          error={fieldError}
          disabled={!start || check.isPending}
        />
        <button
          type="button"
          onClick={() => {
            check.reset();
            send.mutate();
          }}
          disabled={send.isPending || wait > 0}
          className="text-xs font-medium text-accent hover:underline disabled:cursor-default disabled:text-fg-subtle disabled:no-underline"
        >
          {wait > 0 ? `Resend in ${wait}s` : "Resend code"}
        </button>
        {send.isError && <ErrorAlert error={send.error} title="Could not send the code" />}
        {check.isError && !fieldError && (
          <ErrorAlert error={check.error} title={expired ? "Code expired" : "Could not check the code"} />
        )}
      </div>
    </Dialog>
  );
}

/**
 * Step two: the PIN, checked on its own (`verify`, which counts toward the PIN lock) before the
 * final confirmation. `notice` shows when it is asked again, e.g. the PIN failed at the send.
 */
export function PinDialog({
  open,
  verify,
  onVerified,
  onBack,
  onCancel,
  notice,
}: {
  open: boolean;
  verify: (pin: string) => Promise<unknown>;
  onVerified: (pin: string) => void;
  onBack: () => void;
  onCancel: () => void;
  notice?: string | null;
}) {
  const [pin, setPin] = useState("");
  const check = useMutation({
    mutationFn: verify,
    onSuccess: (_, typed) => onVerified(typed),
    onError: () => setPin(""),
  });
  const pinError =
    check.error instanceof ApiError &&
    (check.error.code === "pin_incorrect" || check.error.code === "pin_locked")
      ? check.error.message
      : null;

  // Each showing starts empty.
  useEffect(() => {
    if (!open) {
      setPin("");
      check.reset();
    }
  }, [open, check.reset]);

  const submit = (typed: string) => {
    if (typed.length === 6 && !check.isPending) check.mutate(typed);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title="Enter PIN"
      footer={
        <>
          <Button variant="ghost" onClick={onBack} disabled={check.isPending} className="mr-auto">
            Back
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={check.isPending}>
            Cancel
          </Button>
          <Button onClick={() => submit(pin)} loading={check.isPending} disabled={pin.length < 6}>
            Continue
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-fg">
        {notice && !check.isError && <p className="text-xs text-warn">{notice}</p>}
        <PinBoxes
          label="PIN"
          value={pin}
          onChange={(v) => {
            setPin(v);
            if (check.isError) check.reset();
          }}
          onComplete={submit}
          error={pinError}
          disabled={check.isPending}
        />
        {check.isError && !pinError && <ErrorAlert error={check.error} title="Could not check the PIN" />}
      </div>
    </Dialog>
  );
}
